import React, { useEffect, useRef, useState } from "react";
import { twilioCountries } from "@/components/pages/settings/phone-numbers/util";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FaFax, FaPhone } from "react-icons/fa";
import { FaMessage, FaPhotoFilm } from "react-icons/fa6";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Link } from "react-router-dom";
import { useGetBillingUsageQuery, useGetHomeQuery, useLazyGetAvailablePhoneNumbersQuery } from "@/store/rest_api";
import PhoneNumberSearchParams from "@/model/interface/PhoneNumberSearchParams";
import { Skeleton } from "@/components/ui/skeleton";
import AvailablePhoneNumber from "@/model/interface/AvailablePhoneNumber";
import { formatMoneyAmount } from "@/util/sums";
import { TollFreeBadge, TOLL_FREE_TEXT_CLASS } from "@/components/phone-numbers/TollFreeBadge";
import { BuyPhoneNumberDialog } from "@/components/pages/settings/phone-numbers/BuyPhoneNumberDialog";

export function NewPhoneNumber() {
  const [selectedCountry, setSelectedCountry] = useState<string>(twilioCountries[0].iso);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [numberType, setNumberType] = useState<"local" | "tollfree">("local");
  const [areaCode, setAreaCode] = useState<string>("");
  const [stateFilter, setStateFilter] = useState<string>("");
  const [triggerSearch, { data: availablePhoneNumbers, isFetching, isSuccess }] =
    useLazyGetAvailablePhoneNumbersQuery();
  const [purchasedNumbers, setPurchasedNumbers] = useState<Set<string>>(new Set());
  const [numberToBuy, setNumberToBuy] = useState<AvailablePhoneNumber | null>(null);
  const { data: user } = useGetHomeQuery();
  const { data: usage } = useGetBillingUsageQuery();

  const isTrial = user?.activeOrganisation?.subscriptionStatus === "TRIAL";
  const includedNumbers = usage?.phoneNumbersIncluded;
  const usedNumbers = usage?.phoneNumbersUsed ?? 0;
  const includedNumberLeft =
    includedNumbers === null || (includedNumbers !== undefined && usedNumbers < includedNumbers);
  const trialNumberTaken = isTrial && usage !== undefined && !includedNumberLeft;
  const trialAllowance = includedNumbers === 1 ? "one phone number" : `${includedNumbers} phone numbers`;

  useEffect(() => {
    const searchParams: PhoneNumberSearchParams = {
      countryCode: selectedCountry,
      query: searchQuery,
      type: numberType,
    };
    if (searchQuery.length <= 1) {
      delete searchParams.query;
    }
    const parsedAreaCode = parseInt(areaCode, 10);
    if (!isNaN(parsedAreaCode)) {
      searchParams.areaCode = parsedAreaCode;
    }
    if (stateFilter.trim().length >= 2 && numberType === "local") {
      searchParams.state = stateFilter.trim().toUpperCase();
    }

    triggerSearch(searchParams);
  }, [selectedCountry, searchQuery, numberType, areaCode, stateFilter]);

  function onSearch(event: React.MouseEvent<HTMLButtonElement>) {
    if (searchInputRef.current) {
      setSearchQuery(searchInputRef.current.value);
    }
  }

  function markPurchased(number: string) {
    setPurchasedNumbers((previous) => new Set(previous).add(number));
  }

  return (
    <div className="p-4">
      {usage && isTrial && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-4 text-sm">
          <span>
            {trialNumberTaken
              ? `Your trial includes ${trialAllowance}. Choose a plan to add more.`
              : `Your trial includes ${trialAllowance}.`}
          </span>
          {trialNumberTaken && (
            <Button asChild size="sm">
              <Link to="/manage-organisation/billing/plan">Choose a plan</Link>
            </Button>
          )}
        </div>
      )}
      {!isTrial && includedNumbers != null && (
        <div className="mb-4 rounded-lg border border-primary/40 bg-primary/5 p-4 text-sm tabular-nums">
          {Math.min(usedNumbers, includedNumbers)} of {includedNumbers} included numbers used
          {usedNumbers > includedNumbers && (
            <span className="text-muted-foreground">, {usedNumbers - includedNumbers} billed monthly</span>
          )}
        </div>
      )}

      <div className="mb-4">
        <Label htmlFor="country">Country</Label>
        <Select onValueChange={setSelectedCountry}>
          <SelectTrigger className="w-45">
            <SelectValue placeholder={`${twilioCountries[0].name} (${twilioCountries[0].dial})`} />
          </SelectTrigger>
          <SelectContent id="country" defaultValue={selectedCountry}>
            <SelectGroup>
              <SelectLabel>Select country</SelectLabel>
              {twilioCountries.map(({ name, iso, dial }) => (
                <SelectItem key={iso} value={iso}>
                  {name} ({dial})
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div className="mb-4 mt-4 flex flex-wrap items-end gap-3">
        <div>
          <Label htmlFor="number-type">Number Type</Label>
          <Select value={numberType} onValueChange={(value) => setNumberType(value as "local" | "tollfree")}>
            <SelectTrigger id="number-type" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="local">Local</SelectItem>
              <SelectItem value="tollfree">Toll-free</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="area-code">Area Code</Label>
          <Input
            id="area-code"
            type="text"
            inputMode="numeric"
            placeholder={numberType === "tollfree" ? "e.g. 833" : "e.g. 415"}
            className="w-28"
            value={areaCode}
            onChange={(event) => setAreaCode(event.target.value)}
          />
        </div>
        {numberType === "local" && (
          <div>
            <Label htmlFor="state-filter">State</Label>
            <Input
              id="state-filter"
              type="text"
              placeholder="e.g. CA"
              className="w-24"
              value={stateFilter}
              onChange={(event) => setStateFilter(event.target.value)}
            />
          </div>
        )}
        <div>
          <Label htmlFor="search">Contains Digits</Label>
          <div className="flex items-center space-x-2">
            <Input
              ref={searchInputRef}
              type="text"
              id="search"
              placeholder="Search by digits"
              className="w-48 p-2"
            />
            <Button type="button" onClick={onSearch}>
              Search
            </Button>
          </div>
        </div>
      </div>

      {isFetching && <Skeleton className="w-full h-100" />}

      {!isFetching && isSuccess && availablePhoneNumbers?.length === 0 && (
        <p className="text-sm text-muted-foreground py-6">
          No numbers available for this search.
          {numberType === "tollfree"
            ? " Toll-free numbers are only offered in some countries; try United States or Canada."
            : " Try a different area code, state, or country."}
        </p>
      )}

      {!isFetching && availablePhoneNumbers && availablePhoneNumbers.length > 0 && (
        <Table>
          <TableCaption>A list of available phone numbers</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Region</TableHead>
              <TableHead>Capabilities</TableHead>
              <TableHead>Monthly Fee</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {availablePhoneNumbers?.map((num) => (
              <TableRow key={`number-${num.number}`} className={purchasedNumbers.has(num.number) ? "opacity-50" : ""}>
                <TableCell className={numberType === "tollfree" ? `font-medium ${TOLL_FREE_TEXT_CLASS}` : "font-medium"}>
                  <span className="flex items-center gap-2">
                    {num.number}
                    {numberType === "tollfree" && <TollFreeBadge />}
                  </span>
                </TableCell>
                <TableCell>{num.location}</TableCell>
                <TableCell>{num.region}</TableCell>
                <TableCell className="flex">
                  {num.capabilities.voice && (
                    <span title="Voice" className="inline pt-2 pb-2 ps-1 pe-1">
                      <FaPhone />
                    </span>
                  )}
                  {num.capabilities.sms && (
                    <span title="SMS" className="inline pt-2 pb-2 ps-1 pe-1">
                      <FaMessage />
                    </span>
                  )}
                  {num.capabilities.mms && (
                    <span title="MMS" className="inline pt-2 pb-2 ps-1 pe-1">
                      <FaPhotoFilm />
                    </span>
                  )}
                  {num.capabilities.fax && (
                    <span title="Fax" className="inline pt-2 pb-2 ps-1 pe-1">
                      <FaFax />
                    </span>
                  )}
                </TableCell>
                <TableCell>{includedNumberLeft ? "Included" : formatMoneyAmount(num.monthlyCost)}</TableCell>
                <TableCell className="text-right">
                  <Button
                    onClick={() => setNumberToBuy(num)}
                    disabled={purchasedNumbers.has(num.number) || trialNumberTaken}
                    variant={purchasedNumbers.has(num.number) ? "outline" : "default"}
                  >
                    {purchasedNumbers.has(num.number) ? "Purchased" : "Buy"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {!isFetching && isSuccess && availablePhoneNumbers && availablePhoneNumbers.length === 0 && (
        <div className="mt-4 text-sm text-muted-foreground w-full">No phone numbers found.</div>
      )}

      <BuyPhoneNumberDialog
        open={numberToBuy !== null}
        onOpenChange={(open) => !open && setNumberToBuy(null)}
        availableNumber={numberToBuy}
        numberType={numberType}
        included={includedNumberLeft}
        isTrial={isTrial}
        onPurchased={markPurchased}
      />
    </div>
  );
}
